// 🧿 طلسم محافظ فایل

import React, { createContext } from "react";
// UI Framework
// Hooks
import { useAppSelector } from "src/App/hooks";
// Common Components
import { CheckBox, FileDownloader, Button, Modal } from "src/Components";
import TableTemplate, {
  IExtraTypeUIProps,
  IFilterTableCols,
} from "src/Tables/TableTemplate/tableTemplate";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
// Local Components
import OrderBook from "./OrderBook";
import PairPositionSearchParamUI from "./PairPositionSearchParamUI";
import ExpectedReturnModal from "./ExpectedReturnModal";
import OpenningSettingsModal from "./OpenningSettingsModal";
import FilterSettingModal from "./FilterSettingModal";
import ChartSearchParamUI from "../ChartSearchParamUI";
import { Additional } from "./Additional";
// Requests
import {
  getPaiPositionExcel,
  getEnterPairPositionTable,
} from "src/Requests/coveredCall";
// Utilities
import * as R from "ramda";
// Types
import { ExtraType } from "src/Interfaces/TableInterfaces";
import {
  IEnterPairPositionSearchParams,
  ISelectedTableInfo,
  IEnterPairPositionTableReport,
} from "src/Interfaces/coveredCall";
import RiskProfile from "../Accounts/RiskProfile";
import FixedBasePriceSettingsModal from "./FixedBasePriceSettingsModal";

interface IVisibleHeaders {
  all: boolean;
  mediator: boolean;
}

const fixedBasePriceExcludedSearchParamKeys: (keyof IEnterPairPositionSearchParams)[] =
  [
    "ignoreQueue",
    "minCallITMPercentSide1",
    "maxCallITMPercentSide1",
    "minPutITMPercentSide1",
    "maxPutITMPercentSide1",
    "minBuysideRemainingDay",
    "maxBuysideRemainingDay",
    "minCallDTMSide1",
    "maxCallDTMSide1",
    "minPutDTMSide1",
    "maxPutDTMSide1",
  ];

enum ModalMode {
  Closed,
  ExpectedReturn,
  OpenningSettings,
  FilterSetting,
  FixedBasePriceSettings,
  RiskProfile,
}

export const EnterPairPositionContext = createContext({
  riskProfileId: "",
  useFixedBasePrices: false,
});

interface IProps {
  useFixedBasePrices?: boolean;
}

const EnterPairPosition: React.FC<IProps> = ({
  useFixedBasePrices = false,
}) => {
  // refs
  const prevRowRef = React.useRef<number | null>(null);
  // store
  const riskProfiles = useAppSelector(
    (store) => store.coveredCall.riskProfiles,
  );
  // local store
  const [modalMode, setModalMode] = React.useState(ModalMode.Closed);
  const [searchParam, setSearchParam] =
    React.useState<IEnterPairPositionSearchParams>({
      riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
      useFixedBasePrices,
    } as IEnterPairPositionSearchParams);
  const [serverSearchParams, setServerSearchParams] =
    React.useState<IEnterPairPositionSearchParams>({
      riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
      useFixedBasePrices,
    } as IEnterPairPositionSearchParams);
  const [visibleHeaders, setVisibleHeaders] = React.useState(
    {} as IVisibleHeaders,
  );
  const [selectedTableInfo, setSelectedTableInfo] =
    React.useState<ISelectedTableInfo>({} as ISelectedTableInfo);
  const [refreshFlag, setRefreshFlag] = React.useState(false);

  React.useEffect(() => {
    setSearchParam((previousSearchParam) => ({
      ...previousSearchParam,
      riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
      useFixedBasePrices,
      pairType: useFixedBasePrices ? ["7"] : previousSearchParam.pairType,
    }));
    setServerSearchParams((previousServerSearchParams) => ({
      ...previousServerSearchParams,
      riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
      useFixedBasePrices,
      pairType: useFixedBasePrices
        ? ["7"]
        : previousServerSearchParams.pairType,
    }));
  }, [riskProfiles, useFixedBasePrices]);

  const changeHandler = (name: string, value: any) => {
    setVisibleHeaders({ ...visibleHeaders, [name]: value } as IVisibleHeaders);
  };

  const handleExcelDownload = () => getPaiPositionExcel(searchParam);

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const handleSearch = () => {
    const submittedSearchParam = useFixedBasePrices
      ? {
          ...(R.omit(
            fixedBasePriceExcludedSearchParamKeys,
            searchParam,
          ) as IEnterPairPositionSearchParams),
          pairType: ["7"],
          useFixedBasePrices: true,
        }
      : searchParam;
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      submittedSearchParam,
    );
    setServerSearchParams(cleanObject as IEnterPairPositionSearchParams);
  };

  const getFilterCols = (): IFilterTableCols => {
    const result: IFilterTableCols = {
      show: true,
      tableName: "coveredCallPairPositionTable",
      allowedPositions: [],
    };
    if (visibleHeaders.all) result.allowedPositions.push("DefaultHidden");
    if (visibleHeaders.mediator) result.allowedPositions.push("Mediator");
    return result;
  };

  const riskProfileChangeHandler = (name: string, value: any) => {
    searchParamChangeHandler(name, value);
    setServerSearchParams({ ...serverSearchParams, [name]: value });
  };

  const selectedRiskProfileId = serverSearchParams?.riskProfileId?.toString();

  const UIModal = React.useMemo(() => {
    switch (modalMode) {
      case ModalMode.RiskProfile:
        return <RiskProfile />;
      case ModalMode.ExpectedReturn:
        return (
          <ExpectedReturnModal
            onClose={() => setModalMode(ModalMode.Closed)}
            useFixedBasePrices={useFixedBasePrices}
            initialRiskProfileId={selectedRiskProfileId}
            onCopied={() => setRefreshFlag((prev) => !prev)}
          />
        );
      case ModalMode.FilterSetting:
        return (
          <FilterSettingModal
            onClose={() => setModalMode(ModalMode.Closed)}
            onApplied={() => setRefreshFlag((prev) => !prev)}
            useFixedBasePrices={useFixedBasePrices}
            initialRiskProfileId={selectedRiskProfileId}
            onCopied={() => setRefreshFlag((prev) => !prev)}
          />
        );
      case ModalMode.OpenningSettings:
        return (
          <OpenningSettingsModal
            onClose={() => setModalMode(ModalMode.Closed)}
            useFixedBasePrices={useFixedBasePrices}
            initialRiskProfileId={selectedRiskProfileId}
            onCopied={() => setRefreshFlag((prev) => !prev)}
          />
        );
      case ModalMode.FixedBasePriceSettings:
        return (
          <FixedBasePriceSettingsModal
            onClose={() => setModalMode(ModalMode.Closed)}
            onApplied={() => setRefreshFlag((prev) => !prev)}
          />
        );
    }
    return null;
  }, [modalMode, selectedRiskProfileId, useFixedBasePrices]);

  const getExtraTypeUI = ({
    headerName,
    extraType,
    row,
    cellType,
    translateHeaderName,
  }: IExtraTypeUIProps<IEnterPairPositionTableReport>) => {
    switch (extraType) {
      case ExtraType.WithChart:
        return (
          <ChartSearchParamUI {...{ row, headerName, translateHeaderName }} />
        );

      default:
        return <></>;
    }
  };

  return (
    <>
      {modalMode ? (
        <Modal
          title={modalMode === ModalMode.RiskProfile ? "پروفایل ریسک" : modalMode === ModalMode.ExpectedReturn ? "بازده انتظاری" : modalMode === ModalMode.OpenningSettings ? "تنظیمات ورود" : modalMode === ModalMode.FilterSetting ? "تنظیمات فیلتر" : "قیمت نماد پایه"}
          UI={UIModal}
          onClose={() => setModalMode(ModalMode.Closed)}
          modalBoxClassName={
            modalMode === ModalMode.FixedBasePriceSettings
              ? "!w-[400px] !min-w-0"
              : undefined
          }
        />
      ) : (
        <></>
      )}
      <div className="flex flex-col justify-start items-start gap-2 relative pt-5 mx-auto p-8 w-full">
        <div className="flex justify-start items-center w-full">
          <div className="flex flex-row justify-start items-center gap-5">
            <div className="bg-sky-200 rounded py-1 px-2">
              <CheckBox
                checked={visibleHeaders.all}
                label="جزئیات"
                name="all"
                onChange={changeHandler}
              />
            </div>
            <div className="bg-red-200 rounded py-1 px-2">
              <CheckBox
                checked={visibleHeaders.mediator}
                label="محاسبات"
                name="mediator"
                onChange={changeHandler}
              />
            </div>
            <FileDownloader getFile={handleExcelDownload} />
            {!useFixedBasePrices ? (
              <Button
                onClick={() => setModalMode(ModalMode.RiskProfile)}
                text="پروفایل ریسک"
                variant="outlined"
              />
            ) : null}
            <Button
              onClick={() => setModalMode(ModalMode.ExpectedReturn)}
              text="بازده انتظاری"
              variant="outlined"
            />
            <Button
              onClick={() => setModalMode(ModalMode.OpenningSettings)}
              text="تنظیمات ورود"
              variant="outlined"
            />
            <Button
              onClick={() => setModalMode(ModalMode.FilterSetting)}
              text="تنظیمات فیلتر"
              variant="outlined"
            />
            {useFixedBasePrices ? (
              <>
                <Button
                  onClick={() => setModalMode(ModalMode.FixedBasePriceSettings)}
                  text="قیمت نماد پایه"
                  variant="outlined"
                />
              </>
            ) : null}
          </div>
        </div>
        <OrderBook selectedTableInfo={selectedTableInfo} />
        <div className="flex flex-row justify-start items-start gap-1 w-full mt-7 h-fit">
          <PairPositionSearchParamUI
            searchParam={searchParam}
            searchParamChangeHandler={searchParamChangeHandler}
            onSubmit={handleSearch}
            useFixedBasePrices={useFixedBasePrices}
          />
          <div className="border border-gray-300 rounded flex min-w-0 flex-1 flex-col p-4 justify-center items-center gap-3 overflow-x-hidden">
            <SelectableButtons
              label="ریسک پروفایل"
              name="riskProfileId"
              onChange={riskProfileChangeHandler}
              options={riskProfiles}
              value={serverSearchParams?.riskProfileId}
              hideSelectAll
            />
            <EnterPairPositionContext.Provider
              value={{
                riskProfileId: serverSearchParams?.riskProfileId,
                useFixedBasePrices,
              }}
            >
              <TableTemplate<
                IEnterPairPositionSearchParams,
                IEnterPairPositionTableReport
              >
                TableUI={({
                  reports,
                  onAction,
                  sortingType,
                  setSortingType,
                }) => (
                  <CommonTableUI
                    AdditionalBody={Additional}
                    reports={reports}
                    onAction={onAction}
                    filterCols={getFilterCols()}
                    sortingType={sortingType}
                    setSortingType={setSortingType}
                    refreshFlag={refreshFlag}
                    onTableRowClick={(row) => {
                      if (prevRowRef.current === row.id) return;
                      prevRowRef.current = row.id;
                      setSelectedTableInfo({
                        id: row.id,
                        insIds: [
                          row.instrumentId1,
                          row.instrumentId2,
                          row.instrumentId3,
                          row.instrumentId4,
                        ],
                      });
                    }}
                    activeRowId={
                      selectedTableInfo ? selectedTableInfo?.id : undefined
                    }
                    tableBoxClassName="max-h-[500px]"
                    ref={prevRowRef}
                    getExtraTypeUI={(params: any) =>
                      getExtraTypeUI({ ...params })
                    }
                  />
                )}
                refreshTableTime={30000}
                getDataTable={getEnterPairPositionTable}
                searchParameters={serverSearchParams}
                withSort
              />
            </EnterPairPositionContext.Provider>
          </div>
        </div>
      </div>
    </>
  );
};
export default EnterPairPosition;
